import { useApi, createAPIPOSTPayload } from "../auth";
import { useEffect, useState } from "react";
import Loading from "./Loading";
import { backend_url } from "../constants";
import Stack from "react-bootstrap/Stack";
import MTDBar from "./MTDBar";
import Table from "react-bootstrap/Table";
import StudyDesigner from "./StudyDesigner";
import ShadedMatrix from "./ShadedMatrix";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { toast } from 'react-toastify';

function ExperimentalDesign({ store, isPrinting }) {
  const [loading, setLoading] = useState(false);
  const { apiFetch } = useApi();
  let runObject = store.runObject;
  let object = store.experimentObject;
  if (store.loading || loading) return <Loading />;
  if (!object) return <div>Not found</div>;
  let durationLabel = runObject.duration_label;
  let designerDisabled = !store.endpointObject.has_executed || !store.bmdObject.has_executed;
  function createUserDesign(settings) {
    setLoading(true);
    const payload = createAPIPOSTPayload({ settings: settings });
    const reloadRun = settings["durationLabel"] != durationLabel;
    apiFetch(
      backend_url + `/api/create-user-design/${runObject.uuid}/`,
      payload,
    ).then((res) => {
      if (res.status == 200) {
        setLoading(false);
        res
          .json()
          .then((json) => runInAction(() => (store.experimentObject = json)));
        if (reloadRun) {
          console.log("reload");
          runInAction(() => store.fetchRun(runObject.uuid));
        } else {
          console.log("fetch");
          runInAction(() => store.fetchRelatedObjects());
          setLoading(false);
        }
      }
      else{
        res.json().then(json=>toast.error(json));
      }
    });
  }

  if (!object.has_executed) {
    return <Loading />;
  }
  let doseUnit = runObject.dose_unit;
  let subchronicBarWidth = (object.subchronic_mtd / object.acute_mtd) * 80;
  let chronicBarWidth = (object.chronic_mtd / object.acute_mtd) * 80;
  let trial = object.initial ? object : object.preferred_trial;
  let dose_groups = !object.initial
    ? [...new Set(object.trials.map((x) => x.dose_groups))]
    : [];
  dose_groups = dose_groups.sort((x, y) => y - x);

  let animals = !object.initial
    ? [...new Set(object.trials.map((x) => x.total_animals))]
    : [];
  animals = animals.sort((x, y) => y - x);
  let trend_rates = {};
  let bmd_ratios = {};
  let maxTrendRate = !object.initial
    ? Math.max(
        ...object.trials.map(
          (trial) => 1 - trial.no_trend_count / trial.iterations,
        ),
      )
    : 0;
  let maxBmdRatio = !object.initial
    ? Math.max(...object.trials.map((trial) => trial.bmd_ratio))
    : 0;
  let minTrendRate = !object.initial
    ? Math.min(
        ...object.trials.map(
          (trial) => 1 - trial.no_trend_count / trial.iterations,
        ),
      )
    : 0;
  let minBmdRatio = !object.initial
    ? Math.min(...object.trials.map((trial) => trial.bmd_ratio))
    : 0;
  if (!object.initial) {
    for (var i in object.trials) {
      let test_trial = object.trials[i];
      if (test_trial.dose_groups in trend_rates) {
        trend_rates[test_trial.dose_groups][test_trial.total_animals] =
          1 - test_trial.no_trend_count / test_trial.iterations;
        bmd_ratios[test_trial.dose_groups][test_trial.total_animals] =
          test_trial.bmd_ratio;
      } else {
        trend_rates[test_trial.dose_groups] = {};
        bmd_ratios[test_trial.dose_groups] = {};
        trend_rates[test_trial.dose_groups][test_trial.total_animals] =
          1 - test_trial.no_trend_count / test_trial.iterations;
        bmd_ratios[test_trial.dose_groups][test_trial.total_animals] =
          test_trial.bmd_ratio;
      }
    }
  }
  return (
    <Stack className="outer-design-container" gap={1}>
      {object.initial ? (
        <div className="initial-design">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h4>Initial Study Design</h4>
            <p>
              Initial {durationLabel} study design based on a 200 animal
              half/half study design and maximum doses set based on a 20%
              relative body weight change.
            </p>
          </div>
          <div className="design-summary-boxes">
            <div className="summary-box">
              <div className="summary-box-number">{object.total_animals}</div>
              <div className="summary-box-description">TOTAL ANIMALS</div>
            </div>
            <div className="summary-box">
              <div className="summary-box-number">{object.dose_groups}</div>
              <div className="summary-box-description">DOSE GROUPS</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="initial-design">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h4>Generated Study Design</h4>
            <p>
              Generated {durationLabel} study design based on a{" "}
              {trial.total_animals} animal study with {trial.dose_groups} dose
              groups. Maximum doses set based on a{" "}
              {(object.bmr * 100).toFixed(0)}% {object.organ} {object.endpoint}.
            </p>
          </div>
          <div className="design-summary-boxes">
            <div className="summary-box">
              <div className="summary-box-number">{trial.total_animals}</div>
              <div className="summary-box-description">TOTAL ANIMALS</div>
            </div>
            <div className="summary-box">
              <div className="summary-box-number">{trial.dose_groups}</div>
              <div className="summary-box-description">DOSE GROUPS</div>
            </div>
          </div>
        </div>
      )}
      <Stack
        className="design-container"
        direction={isPrinting ? "vertical" : "horizontal"}
        gap={2}
      >
        <div className="mtd-container">
          <h4 className="mtd-header">Predicted Maximum Tolerated Doses</h4>
          <div className="mtd-dose">
            Acute (14 days)
            <MTDBar
              dose={object.acute_mtd}
              doseUnit={doseUnit}
              fillPercent="80%"
              styleClass="acute-bar"
            />
          </div>
          <div className="mtd-dose">
            Subchronic (90 days)
            <MTDBar
              dose={object.subchronic_mtd}
              doseUnit={doseUnit}
              fillPercent={`${subchronicBarWidth.toFixed(1)}%`}
              styleClass="subchronic-bar"
            />
          </div>
          <div className="mtd-dose">
            Chronic (2 years)
            <MTDBar
              dose={object.chronic_mtd}
              doseUnit={doseUnit}
              fillPercent={`${chronicBarWidth.toFixed(1)}%`}
              styleClass="chronic-bar"
            />
          </div>
        </div>
        <div className="design-doses">
          <h4>Study Protocol</h4>
          <Table>
            <thead>
              <tr>
                <th>Dose</th>
                <th>Number of Animals</th>
              </tr>
            </thead>
            <tbody>
              {trial.doses.map((d) => (
                <tr>
                  <td>
                    {d.dose.toFixed(1)} {doseUnit}
                  </td>
                  <td>{d.n}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Stack>
      {object.initial ? null : (
        <Stack
          className="design-container"
          direction={isPrinting ? "vertical" : "horizontal"}
          gap={2}
        >
          <div className="design-trend-container">
            <h4>Tested Study Design Trends</h4>
            <p>
              How often each study design was able to identify a dose-response
              trend
            </p>
            <ShadedMatrix
              xLabel="Dose Groups"
              yLabel="Total Animals"
              xs={dose_groups}
              ys={animals}
              points={trend_rates}
              minVal={minTrendRate}
              maxVal={maxTrendRate}
              decimals={3}
            />
          </div>
          <div className="design-ratio-container">
            <h4>Tested Study Design BMD/BMDL Ratios</h4>
            <p>
              Average BMD/BMDL ratio for each study design, for simulations with
              a trend.
            </p>
            <ShadedMatrix
              xLabel="Dose Groups"
              yLabel="Total Animals"
              xs={dose_groups}
              ys={animals}
              points={bmd_ratios}
              minVal={minBmdRatio}
              maxVal={maxBmdRatio}
              decimals={2}
              invertOpacity={true}
            />
          </div>
        </Stack>
      )}
      {isPrinting ? null : (
        <StudyDesigner
          initialDuration={durationLabel}
          initialThreshold={object.bmr}
          createUserDesign={createUserDesign}
          disabled={designerDisabled}
        />
      )}
    </Stack>
  );
}

export default observer(ExperimentalDesign);
